import {
  useAdminLinkCategoriesQuery,
  useDeleteAdminLinkCategoryMutation,
  useDeleteAdminLinkMutation,
  useReorderAdminLinkCategoriesMutation,
} from "@/api/adminLinkApi";
import PlusIcon from "@/assets/colorable/plus.svg?react";
import TrashIcon from "@/assets/colorable/trash.svg?react";
import WarningIcon from "@/assets/colorable/warning.svg?react";
import EmptyState from "@/components/Campaign/management/components/EmptyState";
import Button from "@/components/Common/Button";
import ConfirmationModal from "@/components/Common/ConfirmationModal";
import Loader from "@/components/Common/kpi/Loader";
import type {AdminLinkCategorySerializer, AdminLinkSerializer} from "@/generated/DjangoTypes";
import AddAdminLinkCategoryModal from "@/pages/adminLinks/AddAdminLinkCategoryModal";
import AddAdminLinkModal from "@/pages/adminLinks/AddAdminLinkModal";
import AdminLinkCategoryCard from "@/pages/adminLinks/AdminLinkCategoryCard";
import {DndContext, PointerSensor, TouchSensor, useSensor, useSensors} from "@dnd-kit/core";
import {arrayMove, rectSortingStrategy, SortableContext} from "@dnd-kit/sortable";
import {createFileRoute} from "@tanstack/react-router";
import {Check, PencilLine} from "lucide-react";
import {useState} from "react";
import {useTranslation} from "react-i18next";
import {useMedia, useTitle} from "react-use";

export const Route = createFileRoute("/_authenticated/_admin/admins/")({
  component: AdminOverview,
});

function AdminOverview() {
  const {t} = useTranslation(["admin_links", "common"]);

  useTitle(t("page_title"));

  const {data: categories, isLoading} = useAdminLinkCategoriesQuery();
  const {mutate: reorderCategories} = useReorderAdminLinkCategoriesMutation();
  const {mutate: deleteCategory, isPending: isDeletingCategory} = useDeleteAdminLinkCategoryMutation();
  const {mutate: deleteLink, isPending: isDeletingLink} = useDeleteAdminLinkMutation();
  const isMobile = useMedia("(max-width: 640px)");
  const sensors = useSensors(useSensor(isMobile ? TouchSensor : PointerSensor));

  const [isEditMode, setEditMode] = useState(false);
  const [isAddCategoryModalOpen, setAddCategoryModalOpen] = useState(false);
  const [isAddLinkModalOpen, setAddLinkModalOpen] = useState(false);
  const [addLinkDefaultCategoryId, setAddLinkDefaultCategoryId] = useState<number | undefined>(undefined);
  const [categoryToEdit, setCategoryToEdit] = useState<AdminLinkCategorySerializer | null>(null);
  const [linkToEdit, setLinkToEdit] = useState<AdminLinkSerializer | null>(null);
  const [categoryToDelete, setCategoryToDelete] = useState<number | null>(null);
  const [linkToDelete, setLinkToDelete] = useState<AdminLinkSerializer | null>(null);

  return (
    <div className="max-w-wide container mx-auto mt-6 mb-20 flex flex-col gap-6 px-4 sm:mt-8 sm:px-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-1">
          <h1 className="bg-linear-(--primary-dark-gradient) bg-clip-text text-2xl font-bold text-transparent">
            {t("page_title")}
          </h1>
          <span className="text-neutral-grey-dark text-sm">{t("page_description")}</span>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          {isEditMode && (
            <>
              <Button buttonStyle="White" className="h-11 gap-2 px-4" onClick={() => setAddCategoryModalOpen(true)}>
                <PlusIcon className="size-3.5" />
                {t("add_category")}
              </Button>
              <Button
                buttonStyle="Blue"
                className="h-11 gap-2 px-4"
                isDisabled={!categories?.length}
                onClick={() => setAddLinkModalOpen(true)}>
                <PlusIcon className="size-3.5" />
                {t("add_link")}
              </Button>
            </>
          )}
          <Button
            buttonStyle={isEditMode ? "Gradient" : "White"}
            className="h-11 gap-2 px-4"
            onClick={() => setEditMode(prev => !prev)}>
            {isEditMode ? <Check className="size-3.5" /> : <PencilLine className="size-3.5" />}
            {isEditMode ? t("done") : t("common:Edit")}
          </Button>
        </div>
      </div>

      {isEditMode && (
        <div className="bg-primary-powder text-primary-midnight flex items-start gap-2.5 rounded-md p-3 text-sm">
          <WarningIcon className="text-primary-dark-solid mt-0.5 size-4 shrink-0" />
          <div className="flex flex-col gap-1">
            <span>{t("shared_state_notice")}</span>
            <span>{t("protected_items_notice")}</span>
          </div>
        </div>
      )}

      {isLoading && <Loader />}

      {!isLoading && !categories?.length && (
        <EmptyState
          className="md:min-w-full"
          title={t("no_categories_yet")}
          description={t("no_categories_yet_description")}
        />
      )}

      {!!categories?.length && (
        <DndContext
          sensors={sensors}
          onDragEnd={({active, over}) => {
            if (!over || active.id === over.id) return;
            const oldIndex = categories.findIndex(category => category.id === active.id);
            const newIndex = categories.findIndex(category => category.id === over.id);
            reorderCategories(arrayMove(categories, oldIndex, newIndex).map(category => category.id));
          }}>
          <SortableContext items={categories} strategy={rectSortingStrategy}>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 xl:grid-cols-3">
              {categories.map(category => (
                <AdminLinkCategoryCard
                  key={category.id}
                  category={category}
                  isEditMode={isEditMode}
                  onAddLink={() => {
                    setAddLinkDefaultCategoryId(category.id);
                    setAddLinkModalOpen(true);
                  }}
                  onEditCategory={() => setCategoryToEdit(category)}
                  onDeleteCategory={() => setCategoryToDelete(category.id)}
                  onEditLink={setLinkToEdit}
                  onDeleteLink={setLinkToDelete}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {(isAddCategoryModalOpen || !!categoryToEdit) && (
        <AddAdminLinkCategoryModal
          category={categoryToEdit ?? undefined}
          onClose={() => {
            setAddCategoryModalOpen(false);
            setCategoryToEdit(null);
          }}
        />
      )}
      {(isAddLinkModalOpen || !!linkToEdit) && (
        <AddAdminLinkModal
          categories={categories ?? []}
          defaultCategoryId={addLinkDefaultCategoryId}
          link={linkToEdit ?? undefined}
          onClose={() => {
            setAddLinkModalOpen(false);
            setAddLinkDefaultCategoryId(undefined);
            setLinkToEdit(null);
          }}
        />
      )}
      {categoryToDelete !== null && (
        <ConfirmationModal
          Icon={TrashIcon}
          title={t("delete_category_title")}
          description={t("delete_category_confirmation")}
          confirmButtonText={t("common:Delete")}
          isPending={isDeletingCategory}
          onClose={() => setCategoryToDelete(null)}
          onConfirm={() => deleteCategory(categoryToDelete, {onSuccess: () => setCategoryToDelete(null)})}
        />
      )}
      {!!linkToDelete && (
        <ConfirmationModal
          Icon={TrashIcon}
          title={t("delete_link_title")}
          description={t("delete_link_confirmation")}
          confirmButtonText={t("common:Delete")}
          isPending={isDeletingLink}
          onClose={() => setLinkToDelete(null)}
          onConfirm={() => deleteLink(linkToDelete.id, {onSuccess: () => setLinkToDelete(null)})}
        />
      )}
    </div>
  );
}
